import { create } from 'zustand';
import { isFocusFull } from '@cerebro-diem/core';
import { saveTaskEdit } from './tasks';

/** A task asked into a day whose focus is full, waiting for the person to say which one makes room. */
export interface FocusAsk {
  taskId: string;
  taskName: string;
  date: string;
  /** True once room was made and the task is focused; false when the person kept their three. */
  resolve: (focused: boolean) => void;
}

interface FocusChooserState {
  asking: FocusAsk | null;
  ask: (taskId: string, taskName: string, date: string) => Promise<boolean>;
  finish: (focused: boolean) => void;
}

/** The one "Focus holds 3" question (CER-135), shown by FocusChooser over any page. */
export const useFocusChooser = create<FocusChooserState>((set, get) => ({
  asking: null,
  ask: (taskId, taskName, date) =>
    new Promise<boolean>((resolve) => {
      get().asking?.resolve(false);
      set({ asking: { taskId, taskName, date, resolve } });
    }),
  finish: (focused) => {
    get().asking?.resolve(focused);
    set({ asking: null });
  },
}));

/**
 * Focus a task for `date`, or clear its focus with null. A full day is
 * refused by the database and asks which focused task makes room; the
 * result says whether the task is focused now. Any other failure is thrown.
 */
export async function focusTask(id: string, name: string, date: string | null): Promise<boolean> {
  try {
    await saveTaskEdit(id, { focus_date: date });
    return date !== null;
  } catch (error) {
    if (date !== null && isFocusFull(error)) return useFocusChooser.getState().ask(id, name, date);
    throw error;
  }
}
